/**
 * First-touch attribution for signups, without cookies: when a visitor clicks
 * through to the app, the campaign they arrived with travels along in the
 * link's query (utm_*, plus the site, landing page and referring host). The
 * app keeps it with the organization that signs up. Nothing is stored on the
 * device, and nothing personal is added: these are campaign labels and hosts.
 */

/** The app's hosts that receive attribution: the app itself, not any outbound link. */
export function isAppHost(host: string): boolean {
  return /^app\.logpulse\.(io|nl|de|fr|es|it)$/.test(host) || host === 'localhost:5173';
}

/** Another LogPulse marketing domain (a language switch), not the app. */
export function isMarketingHost(host: string): boolean {
  return /^(www\.)?logpulse\.(io|nl|de|fr|es|it)$/.test(host);
}

/**
 * A link to another language domain with the visit's utm_* added, so a paid
 * visitor who switches language is still credited to the campaign there. Only
 * the campaign labels travel: the other site records its own landing page and
 * referrer. Existing parameters win.
 */
export function decorateLocaleUrl(url: URL, utm: Record<string, string>): URL {
  const out = new URL(url.toString());
  for (const [key, value] of Object.entries(utm)) {
    if (value && !out.searchParams.has(key)) out.searchParams.set(key, value.slice(0, 120));
  }
  return out;
}

export interface LandingContext {
  /** utm_* captured on the landing page. */
  utm: Record<string, string>;
  /** The marketing site, e.g. logpulse.nl. */
  site: string;
  /** Path of the first page of the visit. */
  landing: string;
  /** Host of the page that sent the visitor, or empty for direct. */
  referrerHost: string;
}

/** The app URL with the visit's attribution added; existing parameters win. */
export function decorateSignupUrl(url: URL, ctx: LandingContext): URL {
  const out = new URL(url.toString());
  const set = (key: string, value: string | undefined) => {
    if (value && !out.searchParams.has(key)) out.searchParams.set(key, value.slice(0, 120));
  };
  for (const [key, value] of Object.entries(ctx.utm)) set(key, value);
  set('lp_site', ctx.site);
  set('lp_landing', ctx.landing);
  if (ctx.referrerHost && ctx.referrerHost !== ctx.site) set('lp_ref', ctx.referrerHost);
  return out;
}
